import { useState } from 'react'
import classNames from 'classnames/bind'

import { useCustomAccordion } from '../customAccordion/useCustomAccordion'
import { OuterCard } from '../outerCard/OuterCard'
import { LocaleSwitchHeader } from '../localeSwitchHeader/LocaleSwitchHeader'
import { CustomAccordion } from '../customAccordion/CustomAccordion'
import { DEFAULT_ENTRY_SUBMISSION_LOCALES, SubmissionLocale } from '../localeSwitchHeader/LocaleSwitchHeader.types'
import { createMoneyAnswers } from '../../candidateTasks/candidateReview/money/moneyAnswers'

import { formatMoney } from '@percent/utility'
import { Accordion } from '@percent/lemonade'
import styles from './SubmissionDetailsSection.module.scss'
import { SubmissionDetail, SubmissionDetailsProps } from './SubmissionDetailsSection.types'

const cx = classNames.bind(styles)

const OPERATING_BUDGET_AMOUNT_QUESTION_KEY = 'operatingBudgetAmount'

const formatAnswer = (questionKey: string | undefined, answer: string | undefined) => {
  if (!answer) return answer

  if (questionKey?.includes(OPERATING_BUDGET_AMOUNT_QUESTION_KEY)) {
    return formatMoney({ amount: Number(answer), currency: 'USD' })
  }

  return answer
}

export function SubmissionDetailsSection({ details, dataTestId }: Readonly<SubmissionDetailsProps>) {
  const [selectedLocale, setSelectedLocale] = useState<string>(SubmissionLocale.EN_US)
  const [shouldAnimate, setShouldAnimate] = useState(false)

  const moneyAnswers = createMoneyAnswers(
    details
      .filter(detail => detail.questionType === 'money')
      .map(detail => ({ key: detail.questionKey, answer: detail.translations['en-US'] }))
  )

  const visibleDetails = details.filter(
    detail => !(detail.questionType === 'money' && moneyAnswers.isPairedCurrency(detail.questionKey))
  )

  const renderAnswer = ({
    questionKey,
    questionType,
    translations
  }: Pick<SubmissionDetail, 'questionKey' | 'questionType' | 'translations'>) =>
    questionType === 'money' && translations['en-US']
      ? moneyAnswers.format(questionKey, translations['en-US'])
      : formatAnswer(questionKey, translations[selectedLocale])

  const itemsIds = visibleDetails.map(detail => detail.questionId)
  const { toggleAll, handleAccordionChange, getAccordionValue, label } = useCustomAccordion(itemsIds)

  const originalLocale = details[0]?.originalLocale ?? SubmissionLocale.EN_US
  const translationAvailable = details.some(detail => detail.translations[originalLocale])
  const isLanguageOtherThanDefault = !DEFAULT_ENTRY_SUBMISSION_LOCALES.includes(originalLocale)

  const handleLocaleChange = (newLocale: string) => {
    setShouldAnimate(true)
    setSelectedLocale(newLocale)
    setTimeout(() => setShouldAnimate(false), 500)
  }

  const animateTranslation = translationAvailable && isLanguageOtherThanDefault && shouldAnimate

  return (
    <OuterCard
      dataTestId={dataTestId}
      customBgColour={isLanguageOtherThanDefault && translationAvailable ? 'info' : 'default'}
      expandAll={{ label, onClick: toggleAll }}
      customHeader={
        <LocaleSwitchHeader
          selectedLocale={selectedLocale}
          setSelectedLocale={handleLocaleChange}
          originalLocale={originalLocale}
          translationAvailable={translationAvailable}
        />
      }
    >
      <div key={selectedLocale} className={cx(styles.innerCard, styles.defaultBorder)} data-testid="inner-card">
        {visibleDetails.map(({ questionId, questionKey, questionType, questionLabel, translations }, i) => (
          <div
            key={questionId}
            className={cx(
              styles.innerCardSectionAccordion,
              styles.defaultTopBorder,
              animateTranslation && styles.fadeInFromTop
            )}
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <CustomAccordion
              multiple
              value={getAccordionValue(questionId)}
              onChange={handleAccordionChange(questionId)}
            >
              <Accordion.Item value={questionId} data-testid={`question-${questionId}`}>
                <Accordion.Control data-testid={`question-${questionId}-label`}>{questionLabel}</Accordion.Control>
                <Accordion.Panel data-testid={`question-${questionId}-answer`}>
                  {renderAnswer({ questionKey, questionType, translations })}
                </Accordion.Panel>
              </Accordion.Item>
            </CustomAccordion>
          </div>
        ))}
      </div>
    </OuterCard>
  )
}
